import { cloneObj, Module, MUSTNeverHappenException } from '@nu-art/ts-common';
import { dispatcher_OnFindingsContextUpdated } from './dispatcher.js';
import { ModuleFE_Advisor_FindingView, ModuleFE_RuntimeFinding, ModuleFE_RuntimeOrder } from '@app/advisor-frontend';
import { ModuleFE_Inbox } from '../module-inbox/ModuleFE_Inbox.js';
import { StorageKey } from '@nu-art/thunderstorm-frontend/index';
import { RuntimeOrderStatus_Enum } from '@app/advisor-shared';
import { findingViewType_Parent, findingViewType_Sections, sectionRenderMethod_Contained } from '@app/advisor-ui-shared';
import { PanelKey_Findings } from '@app/advisor-v5-shared';
import { ModuleFE_AdvisorMini } from '../ModuleFE_AdvisorMini.js';
import { ModuleFE_AdvisorAnalytics } from '../../_analytics/consts.js';
const Key_FindingPanelStateKey = 'storage_findings-panel-state';
const storageKey_PanelState = new StorageKey(Key_FindingPanelStateKey);
class ModuleFE_PanelFindings_Class extends Module {
    //######################### Class Properties #########################
    cachedPanelState;
    //######################### Life Cycle #########################
    __onAdvisorReset() {
        delete this.cachedPanelState;
        storageKey_PanelState.delete();
    }
    //######################### Internal Logic #########################
    getInitialPanelState = () => {
        const topLevelViews = ModuleFE_Advisor_FindingView.getTopLevelViews();
        const selectedTopLevelView = topLevelViews.find(view => view.label.toLowerCase() === 'h&p') ?? topLevelViews[0];
        const selectedChildViewId = selectedTopLevelView.type === findingViewType_Parent ? selectedTopLevelView.childrenIds[0] : undefined;
        return {
            selectedParentViewId: selectedTopLevelView._id,
            parentViewData: {
                [selectedTopLevelView._id]: {
                    selectedChildViewId,
                }
            }
        };
    };
    setPanelState = (state) => {
        this.cachedPanelState = state;
        storageKey_PanelState.set(state);
        dispatcher_OnFindingsContextUpdated.dispatchUI();
    };
    //######################### Public Set Methods #########################
    setSelectedParentView = (fv) => {
        const findingView = typeof fv === 'string' ? ModuleFE_Advisor_FindingView.cache.unique(fv) : fv;
        if (!findingView)
            throw new MUSTNeverHappenException(`Could not derive finding view ${fv}`);
        //Assert finding view validity
        if (findingView.type !== findingViewType_Parent) {
            const parent = ModuleFE_Advisor_FindingView.cache.find(view => view.type === findingViewType_Parent && view.childrenIds.includes(findingView._id));
            if (parent) {
                this.logError(`Trying to set a selected parent view that isn't top level!`);
                return;
            }
        }
        const state = this.getFindingsPanelState();
        if (state.selectedParentViewId === findingView._id)
            return;
        state.selectedParentViewId = findingView._id;
        if (!state.parentViewData[findingView._id]) {
            state.parentViewData[findingView._id] = {};
            if (findingView.type === findingViewType_Parent)
                state.parentViewData[findingView._id].selectedChildViewId = findingView.childrenIds[0];
        }
        ModuleFE_AdvisorAnalytics.trigger.findings.findings_viewSelect({ viewId: findingView._id, viewLabel: findingView.label });
        this.clearSelectedOrder();
        this.setPanelState(state);
    };
    setSelectedChildView = (fv) => {
        const findingView = typeof fv === 'string' ? ModuleFE_Advisor_FindingView.cache.unique(fv) : fv;
        if (!findingView)
            throw new MUSTNeverHappenException(`Could not derive finding view ${fv}`);
        //Assert finding view validity
        if (findingView.type !== findingViewType_Sections) {
            this.logError(`Trying to set child view which isn't a child ${findingView.label}`);
            return;
        }
        const state = this.getFindingsPanelState();
        const currentParent = ModuleFE_Advisor_FindingView.cache.unique(state.selectedParentViewId);
        if (!currentParent)
            throw new MUSTNeverHappenException(`Could not find view for id ${state.selectedParentViewId}`);
        if (!currentParent.childrenIds?.includes(findingView._id)) {
            this.logError(`Trying to set child view which isn't a child of the current parent view ${currentParent?.label}`);
            return;
        }
        //Set as selected child
        state.parentViewData[currentParent._id].selectedChildViewId = findingView._id;
        //Clear selected section index if the selected view is rendering section in contained method
        if (findingView.sectionRenderMethod === sectionRenderMethod_Contained)
            state.parentViewData[currentParent._id].selectedSectionIndex = undefined;
        ModuleFE_AdvisorAnalytics.trigger.findings.findings_viewSelect({ viewId: findingView._id, viewLabel: findingView.label });
        this.clearSelectedOrder();
        this.setPanelState(state);
    };
    setFindingView = (fv) => {
        const findingView = typeof fv === 'string' ? ModuleFE_Advisor_FindingView.cache.unique(fv) : fv;
        if (!findingView)
            throw new MUSTNeverHappenException(`Could not derive finding view ${fv}`);
        const state = this.getFindingsPanelState();
        if (findingView.type === findingViewType_Parent) {
            const childView = ModuleFE_Advisor_FindingView.cache.unique(findingView.childrenIds[0]);
            if (!childView)
                throw new MUSTNeverHappenException('Setting parent view with no children');
            state.selectedParentViewId = findingView._id;
            state.parentViewData[findingView._id] = {
                selectedChildViewId: childView._id,
            };
        }
        else {
            const parentView = ModuleFE_Advisor_FindingView.cache.find(fv => fv.type === findingViewType_Parent && fv.childrenIds.includes(findingView._id));
            if (!parentView) { // FV does not have a parent
                state.selectedParentViewId = findingView._id;
                state.parentViewData[findingView._id] = {};
            }
            else {
                state.selectedParentViewId = parentView._id;
                state.parentViewData[parentView._id] = {
                    selectedChildViewId: findingView._id,
                };
            }
        }
        ModuleFE_AdvisorAnalytics.trigger.findings.findings_viewSelect({ viewId: findingView._id, viewLabel: findingView.label });
        this.clearSelectedOrder();
        this.setPanelState(state);
    };
    setSelectedSection = (sectionIndex) => {
        const state = this.getFindingsPanelState();
        state.parentViewData[state.selectedParentViewId].selectedSectionIndex = sectionIndex;
        this.clearSelectedOrder();
        this.setPanelState(state);
    };
    jumpToFinding = (rtOrderId) => {
        const relatedFindings = ModuleFE_RuntimeFinding.cache.filter(finding => finding.runtimeOrderId === rtOrderId);
        if (!relatedFindings.length)
            return false;
        const matchingView = ModuleFE_Advisor_FindingView.cache.find(view => {
            if (view.type !== findingViewType_Sections)
                return false;
            return relatedFindings.some(rf => ModuleFE_Advisor_FindingView.findingMatches.view(rf, view));
        });
        //No view matches the finding, return
        if (!matchingView)
            return false;
        const state = this.getFindingsPanelState();
        const parentView = ModuleFE_Advisor_FindingView.cache.find(view => view.type === findingViewType_Parent && view.childrenIds.includes(matchingView._id));
        let indexingId = matchingView._id;
        if (parentView) { //View has a parent
            indexingId = parentView._id;
            state.selectedParentViewId = indexingId;
            state.parentViewData[indexingId] = {
                selectedChildViewId: matchingView._id,
            };
        }
        else { //View is top level but does not have a parent
            state.selectedParentViewId = indexingId;
            state.parentViewData[indexingId] = {};
        }
        if (ModuleFE_AdvisorMini.getIsMiniActive())
            ModuleFE_AdvisorMini.openPanel(PanelKey_Findings);
        this.setPanelState(state);
        return true;
    };
    //######################### Public Get Methods #########################
    getFindingsPanelState = () => {
        if (!this.cachedPanelState) {
            let state = storageKey_PanelState.get();
            if (!state) {
                state = this.getInitialPanelState();
                storageKey_PanelState.set(state);
            }
            this.cachedPanelState = state;
        }
        return cloneObj(this.cachedPanelState);
    };
    getSelectedSectionIndex = () => {
        const state = this.getFindingsPanelState();
        return state.parentViewData[state.selectedParentViewId].selectedSectionIndex;
    };
    getCurrentOnScreenView = () => {
        const state = this.getFindingsPanelState();
        const parentId = state.selectedParentViewId;
        const parentData = state.parentViewData[parentId];
        const resolvedViewId = parentData.selectedChildViewId ?? parentId;
        return ModuleFE_Advisor_FindingView.cache.unique(resolvedViewId);
    };
    //######################### General Public Methods #########################
    clearSelectedOrder = () => {
        const selectedInboxItem = ModuleFE_Inbox.getSelectedInboxItem();
        if (selectedInboxItem?.type !== 'inbox-order')
            return;
        const rtOrder = ModuleFE_RuntimeOrder.cache.unique(selectedInboxItem.itemId);
        if (!rtOrder || rtOrder.orderStatus !== RuntimeOrderStatus_Enum.RESULTED)
            return;
        ModuleFE_Inbox.setSelectedInboxItem();
    };
}
export const ModuleFE_PanelFindings = new ModuleFE_PanelFindings_Class();
//# sourceMappingURL=ModuleFE_PanelFindings.js.map